import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import RsvpModal from "../RsvpModal";
import { COLORS } from "../../constants";

const fieldLabelSx = (isMobile) => ({
  color: COLORS.gray,
  textAlign: "left",
  mb: 0,
  ...(isMobile && { fontSize: "var(--font-size-xxs)" }),
});

const textFieldSx = (isMobile) => ({
  "& .MuiInputBase-input": {
    color: COLORS.white,
    ...(isMobile && { fontSize: "var(--font-size-xxs)" }),
  },
  "& .MuiInput-underline:before": { borderBottom: `1px solid ${COLORS.white}` },
  "& .MuiInput-underline:after": { borderBottom: `2px solid ${COLORS.white}` },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottom: `1px solid ${COLORS.white}`,
  },
});

const selectSx = (isMobile) => ({
  color: COLORS.white,
  borderBottom: `1px solid ${COLORS.white}`,
  borderRadius: 0,
  "& .MuiSelect-select": {
    paddingBottom: "8px",
    ...(isMobile && { fontSize: "var(--font-size-xxs)" }),
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-icon": { color: COLORS.white },
});

// Caps the open dropdown to ~6 items tall and scrolls the rest, rather than letting a long option
// list (e.g. Guests' 0-50) render as one oversized menu. Uses slotProps (MUI v6's current API) rather
// than the legacy PaperProps, which did not visibly take effect in this app's installed MUI version.
const ITEM_HEIGHT = 36;
const menuProps = {
  slotProps: {
    paper: {
      sx: { maxHeight: ITEM_HEIGHT * 6, overflowY: "auto" },
    },
  },
};

const RequiredLabel = ({ label, isMobile }) => (
  <Box sx={{ display: "flex", alignItems: "baseline" }}>
    <Typography variant="body2" sx={fieldLabelSx(isMobile)}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: COLORS.themeRed, ml: 0.5 }}>
      *
    </Typography>
  </Box>
);

const FieldError = ({ error }) =>
  error ? (
    <Typography variant="body2" sx={{ color: COLORS.themeRed, mt: 0.5 }}>
      {error.message}
    </Typography>
  ) : null;

const RsvpForm = ({
  control,
  errors,
  onSubmitClick,
  isRsvpModalOpen,
  onRsvpModalClose,
  createEventResponseCallStatus,
  setSelectedYear,
  isMobile = false,
}) => {
  return (
    <Box sx={{ minHeight: "60vh" }}>
      <Box sx={isMobile ? { mt: -2.5 } : {}}>
        <Box
          component="img"
          src="https://argusblob01.blob.core.windows.net/argus-web/trade-show/TradeShowInvite2026.png"
          alt="Trade Show Invite"
          sx={{
            display: "block",
            // The column now has a real (100%-of-container) width, so a percentage constraint
            // resolves unambiguously — no more auto-sized flex item to conflict with.
            width: "100%",
            height: "auto",
          }}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: COLORS.black,
          py: isMobile ? 2 : 1,
        }}
      >
        <Box sx={{ px: 4 }}>
          <Typography
            variant="h1"
            sx={{
              color: COLORS.themeYellow,
              fontWeight: 600,
              textAlign: "center",
              ...(isMobile && { fontSize: "var(--font-size-xl)" }),
            }}
          >
            !RSVP!
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 4,
              color: COLORS.lightGray,
              textAlign: "center",
              ...(isMobile && { fontSize: "var(--font-size-xxs)" }),
            }}
          >
            Join us for our annual Argus Motor Company Trade Show, showcasing
            innovative automotive solutions!
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <RequiredLabel label="Attending" isMobile={isMobile} />
              <Controller
                name="attending"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    sx={selectSx(isMobile)}
                    error={!!errors.attending}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Maybe">Maybe</MenuItem>
                  </Select>
                )}
              />
              <FieldError error={errors.attending} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <RequiredLabel label="Company Name" isMobile={isMobile} />
              <Controller
                name="companyName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    sx={textFieldSx(isMobile)}
                    error={!!errors.companyName}
                  />
                )}
              />
              <FieldError error={errors.companyName} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <RequiredLabel label="Email" isMobile={isMobile} />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    sx={textFieldSx(isMobile)}
                    error={!!errors.email}
                  />
                )}
              />
              <FieldError error={errors.email} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body2" sx={fieldLabelSx(isMobile)}>
                Account Number
              </Typography>
              <Controller
                name="accountNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    sx={textFieldSx(isMobile)}
                    error={!!errors.accountNumber}
                  />
                )}
              />
              <FieldError error={errors.accountNumber} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <RequiredLabel label="Guests" isMobile={isMobile} />
              <Controller
                name="guests"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    sx={selectSx(isMobile)}
                    error={!!errors.guests}
                    MenuProps={menuProps}
                  >
                    {Array.from({ length: 51 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {i}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FieldError error={errors.guests} />
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mt: 2, py: 2 }}
            >
              <Button
                variant="contained"
                onClick={onSubmitClick}
                sx={{
                  backgroundColor: COLORS.themeYellow,
                  color: COLORS.darkText,
                  fontWeight: 600,
                  borderRadius: "99px",
                  px: 2,
                  py: 1,
                  "&:hover": {
                    backgroundColor: COLORS.themeYellow,
                    opacity: 0.9,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: COLORS.themeYellow,
                    opacity: 0.5,
                    color: COLORS.darkText,
                  },
                }}
              >
                RSVP
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <RsvpModal
        open={isRsvpModalOpen}
        onClose={onRsvpModalClose}
        createEventResponseCallStatus={createEventResponseCallStatus}
        setSelectedYear={setSelectedYear}
      />
    </Box>
  );
};

export default RsvpForm;
